import { ChangeDetectionStrategy, Component, Injector, OnInit, ViewChild } from "@angular/core";
import { CulturalExclusion } from "@common/ADAPT.Common.Model/organisation/cultural-exclusion";
import { CulturalLeadership } from "@common/ADAPT.Common.Model/organisation/cultural-leadership";
import { CulturalRelationship } from "@common/ADAPT.Common.Model/organisation/cultural-relationship";
import { RoleConnection } from "@common/ADAPT.Common.Model/organisation/role-connection";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { ErrorHandlingUtilities } from "@common/lib/utilities/error-handling-utilities";
import { DialogResolveData } from "@common/ux/adapt-common-dialog/base-dialog.component/base-dialog.component";
import { BaseDialogWithDiscardConfirmationComponent } from "@common/ux/adapt-common-dialog/base-dialog-with-discard-confirmation.component/base-dialog-with-discard-confirmation.component";
import { IDxListItemDeletedEvent } from "@common/ux/dx.types";
import { CulturalLeadershipQueryUtilities } from "@org-common/lib/cultural-leadership/cultural-leadership-query-utilities";
import { SelectPersonComponent } from "@org-common/lib/directory-shared/select-person/select-person.component";
import { CulturalLeadershipFrameworkService } from "app/features/culture/cultural-leadership/cultural-leadership-framework.service";
import { lastValueFrom } from "rxjs";

@Component({
    selector: "adapt-edit-cultural-exclusions-dialog",
    standalone: false,
    templateUrl: "./edit-cultural-exclusions-dialog.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: "./edit-cultural-exclusions-dialog.component.scss",
})
export class EditCulturalExclusionsDialogComponent extends BaseDialogWithDiscardConfirmationComponent<void> implements OnInit {
    public readonly dialogName = "EditCulturalExclusionsDialog";

    public configuration!: CulturalLeadership;
    public culturalExclusions: CulturalExclusion[] = [];
    public checkboxConfirmation = false;

    private removedCulturalExclusions: CulturalExclusion[] = [];
    private personRemovedCulturalRelationships = new Map<Person, CulturalRelationship[]>();
    private updatedRoleConnections: RoleConnection[] = [];

    @ViewChild(SelectPersonComponent) public selectPersonComponent?: SelectPersonComponent;

    public constructor(
        injector: Injector,
        protected commonDataService: CommonDataService,
        private clfService: CulturalLeadershipFrameworkService,
    ) {
        super(injector, DialogResolveData.NotRequired);
    }

    public async ngOnInit() {
        this.culturalExclusions = await new CulturalLeadershipQueryUtilities(this.commonDataService).promiseToGetCulturalExclusions();
        this.configuration = await this.clfService.promiseToGetCulturalLeadershipConfiguration();
    }

    public get entitiesToConfirm() {
        return [
            ...this.culturalExclusions,
            ...this.removedCulturalExclusions,
            ...Array.from(this.personRemovedCulturalRelationships.values()).flat(),
            ...this.updatedRoleConnections,
        ];
    }

    public get culturalLeadersForExcludedPeople() {
        return ArrayUtilities.distinct(Array.from(this.personRemovedCulturalRelationships.values())
            .flatMap((culturalRelationships) => culturalRelationships.map((cr) => cr.culturalLeader)));
    }

    public get cohortsToBeEnded() {
        return this.culturalLeadersForExcludedPeople.filter((cl) => cl.activeCulturalCohort.length === 0);
    }

    public personHasCulturalLeader(person: Person) {
        const removedRelationships = this.personRemovedCulturalRelationships.get(person) ?? [];
        return removedRelationships.length > 0;
    }

    @Autobind
    public filterExcludedPeople(person: Person) {
        // don't allow inactive/non-employee people to be excluded (as they are not included in the CLF)
        if (!person.getLatestEmployeeConnection()?.isActive()) {
            return false;
        }

        return !this.culturalExclusions.find((ce) => ce.personId === person.personId);
    }

    public async onPersonSelected(person?: Person) {
        if (!person) {
            return;
        }

        // this exclusion was already deleted, just revert those changes
        let culturalExclusion = this.removedCulturalExclusions.find((cr) => cr.personId === person.personId);
        if (culturalExclusion) {
            culturalExclusion.entityAspect.rejectChanges();
            ArrayUtilities.removeElementFromArray(culturalExclusion, this.removedCulturalExclusions);
        } else {
            culturalExclusion = await this.clfService.promiseToAddCulturalExclusion(person);
        }

        ArrayUtilities.addElementIfNotAlreadyExists(this.culturalExclusions, culturalExclusion);

        // store the removed relationships so we can get the affected cultural leaders later
        const removedRelationships = this.personRemovedCulturalRelationships.get(person) ?? [];

        // remove all their active cultural relationships
        for (const culturalRelationship of person.culturalLeaderRelationships) {
            if (culturalRelationship.isActive()) {
                await this.clfService.promiseToRemoveCulturalRelationship(culturalRelationship);
                ArrayUtilities.addElementIfNotAlreadyExists(removedRelationships, culturalRelationship);
            }
        }

        this.personRemovedCulturalRelationships.set(person, removedRelationships);

        this.selectPersonComponent?.reset();
    }


    public async removeCulturalExclusion(e: IDxListItemDeletedEvent<CulturalExclusion>) {
        if (e.itemData) {
            // have to get these before we make changes
            const isAdded = e.itemData.entityAspect.entityState.isAdded();
            const personRelationships = this.personRemovedCulturalRelationships.get(e.itemData.person) ?? [];

            await lastValueFrom(this.commonDataService.remove(e.itemData));
            if (!isAdded) {
                ArrayUtilities.addElementIfNotAlreadyExists(this.removedCulturalExclusions, e.itemData!);
            } else {
                for (const culturalRelationship of personRelationships) {
                    culturalRelationship.entityAspect.rejectChanges();
                }
            }

            ArrayUtilities.removeElementFromArray(e.itemData!, this.culturalExclusions);

            this.personRemovedCulturalRelationships.delete(e.itemData.person);

            this.selectPersonComponent?.reset();
        }
    }

    public async saveAndClose() {
        this.setErrorMessage(undefined);

        const cohortsToEnd = this.cohortsToBeEnded;
        for (const culturalLeader of cohortsToEnd) {
            const roleConnection = await this.clfService.promiseToEndCulturalLeaderRoleConnection(culturalLeader);
            if (roleConnection) {
                ArrayUtilities.addElementIfNotAlreadyExists(this.updatedRoleConnections, roleConnection);
            }
        }

        try {
            await lastValueFrom(this.commonDataService.saveEntities(this.entitiesToConfirm));
        } catch (e) {
            this.setErrorMessage(ErrorHandlingUtilities.getHttpResponseMessage(e));
        }

        this.resolve();
    }
}
